<div class="w-full">
  <!-- Stats Overview Cards -->
  <div class="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-6 mb-8">
    @if (isLoadingStats) {
      @for (i of [1, 2, 3, 4]; track i) {
        <div class="glass-card flex items-center p-4 rounded-2xl animate-pulse">
          <div class="w-14 h-14 rounded-[14px] bg-white/10 mr-4"></div>
          <div class="flex-1 space-y-2">
            <div class="h-7 bg-white/10 rounded w-16"></div>
            <div class="h-4 bg-white/8 rounded w-28"></div>
          </div>
        </div>
      }
    } @else {
      @for (stat of stats; track stat.title) {
        <div class="glass-card flex items-center p-4 rounded-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
          <div class="stat-icon w-14 h-14 rounded-[14px] flex items-center justify-center mr-4 text-white">
            <span class="material-icons text-2xl">{{ stat.icon }}</span>
          </div>
          <div class="flex-1">
            <div class="flex items-center gap-2">
              <h2 class="text-[1.75rem] font-bold text-text-primary m-0">{{ stat.value }}</h2>
              <span class="ml-auto inline-block text-xs font-semibold text-emerald-400 bg-emerald-400/15 px-2 py-1 rounded-full">Heute</span>
            </div>
            <p class="text-sm text-text-muted">{{ stat.title }}</p>
          </div>
        </div>
      }
    }
  </div>

  <!-- Recent Activity Section -->
  <div class="glass-card p-6 rounded-2xl">
    <h2 class="text-xl font-semibold text-text-primary mb-4">Abgeschlossene Lieferzettel (letzte 30 Tage)</h2>

    <div class="mb-8">
      @if (isLoadingStats) {
        <div class="h-[300px] bg-white/5 flex items-center justify-center rounded-[14px] border border-dashed border-white/15">
          <div class="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
        </div>
      } @else if (chartData.length === 0) {
        <div class="h-[300px] bg-white/5 flex items-center justify-center rounded-[14px] border border-dashed border-white/15">
          <p class="text-sm text-text-muted">Keine Daten vorhanden</p>
        </div>
      } @else {
        <div class="bg-white/5 rounded-[14px] border border-white/8 p-4">
          <div class="flex items-end gap-[3px] h-[250px]">
            @for (day of chartData; track day.date) {
              <div class="flex-1 flex flex-col items-center justify-end h-full group relative">
                <div
                  class="absolute -top-7 left-1/2 -translate-x-1/2 bg-surface-dark text-text-primary text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none"
                >
                  {{ day.count }} Lieferzettel – {{ formatChartDate(day.date) }}
                </div>
                <div
                  class="w-full rounded-t-sm transition-all duration-200 group-hover:opacity-80"
                  [style.height]="getBarHeight(day.count)"
                  [class]="day.count > 0 ? 'bg-primary' : 'bg-white/10'"
                ></div>
              </div>
            }
          </div>
          <div class="flex gap-[3px] mt-2">
            @for (day of chartData; track day.date; let i = $index) {
              <div class="flex-1 text-center">
                @if (i % 5 === 0 || i === chartData.length - 1) {
                  <span class="text-[10px] text-text-muted">{{ formatChartDate(day.date) }}</span>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Recent Delivery Notes Table -->
    <div class="bg-white/5 rounded-[14px] p-4 border border-white/8">
      <h3 class="text-base font-semibold text-text-primary mb-3">Zuletzt bearb. Lieferzettel</h3>

      @if (isLoadingDeliveryNotes) {
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
        </div>
      } @else if (recentDeliveryNotes.length === 0) {
        <div class="py-8 text-center text-text-muted">
          <span class="material-icons text-3xl mb-2 block opacity-50">inbox</span>
          Keine Lieferzettel vorhanden
        </div>
      } @else {
        <table class="w-full border-collapse">
          <thead>
            <tr>
              <th class="px-4 py-3.5 text-left text-sm font-medium text-text-secondary bg-white/6 border-b border-white/10 first:rounded-tl-[10px]">
                Lieferzettel-Nr.
              </th>
              <th class="px-4 py-3.5 text-left text-sm font-medium text-text-secondary bg-white/6 border-b border-white/10">Auftrags-Nr.</th>
              <th class="px-4 py-3.5 text-left text-sm font-medium text-text-secondary bg-white/6 border-b border-white/10">Mitarbeiter</th>
              <th class="px-4 py-3.5 text-left text-sm font-medium text-text-secondary bg-white/6 border-b border-white/10">Positionen</th>
              <th class="px-4 py-3.5 text-left text-sm font-medium text-text-secondary bg-white/6 border-b border-white/10">Status</th>
              <th class="px-4 py-3.5 text-left text-sm font-medium text-text-secondary bg-white/6 border-b border-white/10 last:rounded-tr-[10px]">
                Aktualisiert am
              </th>
            </tr>
          </thead>
          <tbody>
            @for (note of recentDeliveryNotes; track note._id; let last = $last) {
              <tr
                class="transition-colors hover:bg-white/5 cursor-pointer"
                [class.border-b]="!last"
                [class.border-white/6]="!last"
                (click)="openOverviewModal(note)"
              >
                <td class="px-4 py-3.5 text-sm font-medium text-text-primary">{{ note.noteNumber }}</td>
                <td class="px-4 py-3.5 text-sm text-text-secondary">
                  <span class="text-xs bg-white/8 px-2 py-1 rounded">{{ note.order.internalOrderId }}</span>
                </td>
                <td class="px-4 py-3.5 text-sm text-text-secondary">{{ note.employeeId }}</td>
                <td class="px-4 py-3.5 text-sm text-text-secondary">
                  <span class="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-white/8"> {{ getPositionsCount(note) }} Pos. </span>
                </td>
                <td class="px-4 py-3.5 text-sm">
                  <span class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold" [ngClass]="getStatusClass(note.status)">
                    {{ getStatusName(note.status) }}
                  </span>
                </td>
                <td class="px-4 py-3.5 text-sm text-text-secondary">{{ formatDate(note.updatedAt) }}</td>
              </tr>
            }
          </tbody>
        </table>
      }
    </div>
  </div>
</div>

<!-- Delivery Note Overview Modal -->
@if (showOverviewModal) {
  <app-delivery-note-overview-modal
    [deliveryNote]="selectedDeliveryNote"
    [orderInfo]="selectedOrderInfo"
    (close)="closeOverviewModal()"
    (edit)="onEditFromModal($event)"
  ></app-delivery-note-overview-modal>
}
